import { preloadImage, onDocumentLoaded } from '@theme/utilities';

/** @type {Record<string, number>} */
const cache = {};

const shopName = window.Shopify.shop;
// We want to encode shop-url to an integer dividable by Theme.placeholder.general.length
// to get a starting number that is unique to the shop
const shopPlaceholderSeed = shopName.split('-').reduce((acc, char) => acc + char.charCodeAt(0), 0);
const counters = {
  product: 0,
  general: shopPlaceholderSeed % Theme.placeholders.general.length,
};

const isEditorRequest = window.Shopify.visualPreviewMode || window.Shopify.designMode;

if (isEditorRequest) {
  onDocumentLoaded(() => {
    const placeholderUrls = Object.values(Theme.placeholders).flat();

    for (const url of placeholderUrls) {
      preloadImage(url);
    }
  });
}

class PlaceholderImage extends HTMLElement {
  connectedCallback() {
    if (this.hasChildNodes()) return;

    const type = /** @type {'product' | 'general'} */ (this.dataset.type);
    const blockId = this.dataset.blockId;
    if (!type || !(type in counters) || !blockId) return;

    const cached = cache[blockId];
    if (cached != null && Theme.placeholders[type][cached]) {
      const image = Theme.placeholders[type][cached];
      const imageElement = document.createElement('img');
      imageElement.src = image;
      imageElement.alt = Theme.translations.placeholder_image || 'Placeholder Image';
      this.appendChild(imageElement);
      return;
    }

    const counter = counters[type] || 0;
    counters[type] = (counter + 1) % Theme.placeholders[type].length;

    const image = Theme.placeholders[type][counter];
    if (!image) return;

    const sectionElement = this.closest('section');
    const sectionIndex = sectionElement
      ? Array.from(sectionElement.parentElement?.children ?? []).indexOf(sectionElement)
      : -1;

    const imageElement = document.createElement('img');
    imageElement.src = image;
    imageElement.alt = Theme.translations.placeholder_image || 'Placeholder Image';

    if (sectionIndex === -1 || sectionIndex > 3 || isEditorRequest) {
      imageElement.loading = 'lazy';
    }

    this.appendChild(imageElement);
    cache[blockId] = counter;
  }
}

if (!customElements.get('placeholder-image')) {
  customElements.define('placeholder-image', PlaceholderImage);
}

/**
 * Horizon v4 renders Shopify's grey `placeholder_svg_tag` SVGs at the 8 sites where our v2 fork
 * emitted `<placeholder-image>`. Swap them back to our lens textures here rather than re-editing
 * those 8 upstream files, so the next Horizon merge cannot quietly drop the feature again.
 *
 * ponytail: one MutationObserver covers section re-renders and cart-drawer morphs too; no
 * per-site hooks to keep in sync.
 */
// `placeholder_svg_tag: '<class>'` REPLACES the default `placeholder-svg` class, and the v4 media, image and hero
// sites (v2 <placeholder-image> sites) pass one - QA-19: PDP 'Why choose' panel showed Shopify's grey shirt;
// collection heroes without an image showed the stock 'hero-apparel-1' drawing.
const PLACEHOLDER_SVGS =
  'svg.placeholder-svg, svg.media-block__placeholder, svg.image-block__placeholder, svg.hero__media';

function swapPlaceholderSvgs(/** @type {ParentNode} */ root) {
  for (const svg of root.querySelectorAll?.(PLACEHOLDER_SVGS) ?? []) {
    // only card-gallery used the product shapes; every other site used the textures
    const type = svg.closest('.card-gallery, product-card, .product-card') ? 'product' : 'general';
    const images = Theme.placeholders?.[type];
    if (!images?.length) continue;

    const counter = counters[type] || 0;
    counters[type] = (counter + 1) % images.length;

    const image = document.createElement('img');
    image.src = images[counter];
    image.alt = Theme.translations.placeholder_image || 'Placeholder Image';
    // the hero is above the fold: don't lazy-load it (live's hero placeholder was not lazy)
    image.loading = svg.matches('.hero__media') ? 'eager' : 'lazy';
    image.className = svg.getAttribute('class') ?? '';
    svg.replaceWith(image);
  }
}

// Run as soon as this deferred module executes: the document is fully parsed by then. Waiting for window
// 'load' left Shopify's grey drawing on screen for 8-25 s behind the third-party apps (QA-19); live's
// <placeholder-image> painted the texture before DOMContentLoaded.
swapPlaceholderSvgs(document);
new MutationObserver((records) => {
  for (const record of records) {
    for (const node of record.addedNodes) {
      if (node instanceof Element) swapPlaceholderSvgs(node.matches(PLACEHOLDER_SVGS) ? node.parentNode ?? document : node);
    }
  }
}).observe(document.body, { childList: true, subtree: true });
